import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  LogOut, 
  Menu, 
  X,
  Shield,
  User as UserIcon,
  FolderOpen,
  Archive,
  Bell,
  Settings,
  Download,
  Upload,
  ChevronDown,
  Cloud
} from 'lucide-react';
import { authService, notificationService, backupService } from '../../services/api';

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [leadsDropdownOpen, setLeadsDropdownOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [notificationCount, setNotificationCount] = useState(0);
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const settingsRef = useRef(null);
  const leadsRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        setUser(JSON.parse(userStr));
      } catch (error) {
        console.error('Error parsing user:', error);
      }
    }
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setSettingsOpen(false);
      }
      if (leadsRef.current && !leadsRef.current.contains(event.target)) {
        setLeadsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Poll notification count every 60 seconds
  useEffect(() => {
    const fetchNotificationCount = async () => {
      try {
        const response = await notificationService.getCount();
        setNotificationCount(response.data.data.count);
      } catch (error) {
        console.error('Error fetching notification count:', error);
      }
    };

    fetchNotificationCount();
    const interval = setInterval(fetchNotificationCount, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    try {
      await authService.logout();
      localStorage.clear();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.clear();
      navigate('/login');
    }
  };

  const handleBackup = async () => {
    try {
      setIsBackingUp(true);
      const response = await backupService.createBackup();
      
      const blob = new Blob([response.data], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `moahi-backup-${new Date().toISOString().split('T')[0]}.enc`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      alert('✅ Backup created successfully! File is encrypted with AES-256.');
      setSettingsOpen(false);
    } catch (error) {
      console.error('Backup error:', error);
      alert('❌ Failed to create backup: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsBackingUp(false);
    }
  };

  const handleRestoreClick = () => {
    fileInputRef.current?.click();
  };

  const handleRestoreFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.enc')) {
      alert('❌ Invalid backup file. Please select an encrypted backup file (.enc)');
      return;
    }

    const confirmed = window.confirm(
      '⚠️ WARNING: This will restore all data from the backup file.\n\n' +
      'Current data will be replaced!\n\n' +
      'Are you sure you want to continue?'
    );

    if (!confirmed) {
      event.target.value = '';
      return;
    }

    try {
      setIsRestoring(true);
      const formData = new FormData();
      formData.append('backup', file);

      await backupService.restoreBackup(formData);
      
      alert('✅ Backup restored successfully! The page will reload.');
      window.location.reload();
    } catch (error) {
      console.error('Restore error:', error);
      alert('❌ Failed to restore backup: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsRestoring(false);
      event.target.value = '';
    }
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isLeadsActive = () => {
    return isActive('/admin/leads') || isActive('/leads/');
  };

  const isAdmin = user?.role === 'administrator';

  return (
    <nav className="bg-slate-900 border-b border-slate-800">
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo/Brand */}
          <Link to="/" className="flex items-center gap-3">
            <div className="w-16 h-16 bg-gradient-to-br from-[#508742] to-[#3A6B30] rounded-2xl flex items-center justify-center shadow-lg shadow-[#508742]/20">
              <img src="/src/Moahi_white.png"
                   alt="Logo"
                   className="w-40 h-40 object-contain"/>
            </div>
            <div>
              <h1 className="text-white font-bold text-lg">Moahi Sustainable Solutions</h1>
              <p className="text-slate-400 text-xs">Sales Tracker</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all ${
                isActive('/') && location.pathname === '/'
                  ? 'bg-[#508742] text-white'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <LayoutDashboard className="w-4 h-4" />
              Dashboard
            </Link>

            {/* Leads Dropdown */}
            <div className="relative" ref={leadsRef}>
              <button
                onClick={() => setLeadsDropdownOpen(!leadsDropdownOpen)}
                className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all ${
                  isLeadsActive()
                    ? 'bg-amber-500 text-white'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <FileText className="w-4 h-4" />
                Leads
                <ChevronDown className={`w-3 h-3 transition-transform ${leadsDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {leadsDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50">
                  <div className="p-2">
                    <Link
                      to="/admin/leads"
                      onClick={() => setLeadsDropdownOpen(false)}
                      className="block px-4 py-3 text-slate-300 hover:bg-slate-700 hover:text-white rounded-lg transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-4 h-4 text-slate-400" />
                        <div>
                          <p className="text-sm font-medium">All Leads</p>
                          <p className="text-xs text-slate-500">View all sales leads</p>
                        </div>
                      </div>
                    </Link>

                    <Link
                      to="/leads/blue-sky"
                      onClick={() => setLeadsDropdownOpen(false)}
                      className="block px-4 py-3 text-slate-300 hover:bg-slate-700 hover:text-white rounded-lg transition-colors mt-1"
                    >
                      <div className="flex items-center gap-3">
                        <Cloud className="w-4 h-4 text-cyan-400" />
                        <div>
                          <p className="text-sm font-medium">Blue Sky</p>
                          <p className="text-xs text-slate-500">Initial stages</p>
                        </div>
                      </div>
                    </Link>

                    <Link
                      to="/leads/proposal-phase"
                      onClick={() => setLeadsDropdownOpen(false)}
                      className="block px-4 py-3 text-slate-300 hover:bg-slate-700 hover:text-white rounded-lg transition-colors mt-1"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-4 h-4 text-amber-400" />
                        <div>
                          <p className="text-sm font-medium">Proposal Phase</p>
                          <p className="text-xs text-slate-500">Proposals in progress</p>
                        </div>
                      </div>
                    </Link>

                    <Link
                      to="/leads/negotiation"
                      onClick={() => setLeadsDropdownOpen(false)}
                      className="block px-4 py-3 text-slate-300 hover:bg-slate-700 hover:text-white rounded-lg transition-colors mt-1"
                    >
                      <div className="flex items-center gap-3">
                        <Users className="w-4 h-4 text-green-400" />
                        <div>
                          <p className="text-sm font-medium">Negotiation</p>
                          <p className="text-xs text-slate-500">Final stages</p>
                        </div>
                      </div>
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/projects"
              className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all ${
                isActive('/projects')
                  ? 'bg-blue-500 text-white'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <FolderOpen className="w-4 h-4" />
              Projects
            </Link>

            <Link
              to="/archived"
              className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all ${
                isActive('/archived')
                  ? 'bg-purple-500 text-white'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <Archive className="w-4 h-4" />
              Archived
            </Link>

            {isAdmin && (
              <Link
                to="/admin/users"
                className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all ${
                  isActive('/admin/users')
                    ? 'bg-amber-500 text-white'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <Users className="w-4 h-4" />
                Users
              </Link>
            )}
          </div>

          {/* User Menu */}
          <div className="hidden md:flex items-center gap-4">
            {/* Notification Badge */}
            <Link
              to="/notifications"
              className="relative p-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-all"
              title="Notifications"
            >
              <Bell className="w-5 h-5" />
              {notificationCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {notificationCount > 9 ? '9+' : notificationCount}
                </span>
              )}
            </Link>

            {/* Settings Dropdown - Admin Only */}
            {isAdmin && (
              <div className="relative" ref={settingsRef}>
                <button
                  onClick={() => setSettingsOpen(!settingsOpen)}
                  className="p-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-all flex items-center gap-1"
                  title="Settings"
                >
                  <Settings className="w-5 h-5" />
                  <ChevronDown className={`w-3 h-3 transition-transform ${settingsOpen ? 'rotate-180' : ''}`} />
                </button>

                {settingsOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50">
                    <div className="p-2">
                      <div className="px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                        Data Management
                      </div>
                      
                      <button
                        onClick={handleBackup}
                        disabled={isBackingUp}
                        className="w-full px-3 py-2 text-left text-slate-300 hover:bg-slate-700 hover:text-white rounded-lg transition-colors flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Download className="w-4 h-4 text-green-400" />
                        <div>
                          <p className="text-sm font-medium">
                            {isBackingUp ? 'Creating Backup...' : 'Backup Data'}
                          </p>
                          <p className="text-xs text-slate-500">
                            Export encrypted backup
                          </p>
                        </div>
                      </button>

                      <button
                        onClick={handleRestoreClick}
                        disabled={isRestoring}
                        className="w-full px-3 py-2 text-left text-slate-300 hover:bg-slate-700 hover:text-white rounded-lg transition-colors flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed mt-1"
                      >
                        <Upload className="w-4 h-4 text-blue-400" />
                        <div>
                          <p className="text-sm font-medium">
                            {isRestoring ? 'Restoring...' : 'Restore Data'}
                          </p>
                          <p className="text-xs text-slate-500">
                            Import encrypted backup
                          </p>
                        </div>
                      </button>

                      <div className="mt-2 pt-2 border-t border-slate-700">
                        <p className="px-3 py-1 text-xs text-slate-500">
                          🔒 AES-256 Encryption
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Hidden file input for restore */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".enc"
              onChange={handleRestoreFile}
              className="hidden"
            />

            {/* User Info */}
            <div className="flex items-center gap-3 px-4 py-2 bg-slate-800 rounded-lg">
              <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center">
                {isAdmin ? (
                  <Shield className="w-4 h-4 text-white" />
                ) : (
                  <UserIcon className="w-4 h-4 text-white" />
                )}
              </div>
              <div>
                <p className="text-white text-sm font-medium">{user?.fullName || 'User'}</p>
                <p className="text-slate-400 text-xs">
                  {isAdmin ? 'Administrator' : 'Consultant'}
                </p>
              </div>
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg font-medium flex items-center gap-2 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-slate-400 hover:text-white transition-colors"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pt-4 border-t border-slate-800 space-y-2">
            <div className="flex items-center gap-3 px-4 py-3 bg-slate-800 rounded-lg mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center">
                {isAdmin ? (
                  <Shield className="w-5 h-5 text-white" />
                ) : (
                  <UserIcon className="w-5 h-5 text-white" />
                )}
              </div>
              <div>
                <p className="text-white font-medium">{user?.fullName || 'User'}</p>
                <p className="text-slate-400 text-sm">
                  {isAdmin ? 'Administrator' : 'Consultant'}
                </p>
              </div>
            </div>

            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className={`block px-4 py-3 rounded-lg font-medium flex items-center gap-2 transition-all ${
                isActive('/') && location.pathname === '/'
                  ? 'bg-[#508742] text-white'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <LayoutDashboard className="w-5 h-5" />
              Dashboard
            </Link>

            {/* Mobile Leads Menu */}
            <div className="space-y-1">
              <p className="px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                Leads
              </p>
              <Link
                to="/admin/leads"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 rounded-lg font-medium flex items-center gap-2 transition-all text-slate-300 hover:bg-slate-800 hover:text-white"
              >
                <FileText className="w-5 h-5" />
                All Leads
              </Link>
              <Link
                to="/leads/blue-sky"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 rounded-lg font-medium flex items-center gap-2 transition-all text-slate-300 hover:bg-slate-800 hover:text-white"
              >
                <Cloud className="w-5 h-5 text-cyan-400" />
                Blue Sky
              </Link>
              <Link
                to="/leads/proposal-phase"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 rounded-lg font-medium flex items-center gap-2 transition-all text-slate-300 hover:bg-slate-800 hover:text-white"
              >
                <FileText className="w-5 h-5 text-amber-400" />
                Proposal Phase
              </Link>
              <Link
                to="/leads/negotiation"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 rounded-lg font-medium flex items-center gap-2 transition-all text-slate-300 hover:bg-slate-800 hover:text-white"
              >
                <Users className="w-5 h-5 text-green-400" />
                Negotiation
              </Link>
            </div>

            <Link
              to="/projects"
              onClick={() => setMobileMenuOpen(false)}
              className={`block px-4 py-3 rounded-lg font-medium flex items-center gap-2 transition-all ${
                isActive('/projects')
                  ? 'bg-blue-500 text-white'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <FolderOpen className="w-5 h-5" />
              Projects
            </Link>

            <Link
              to="/archived"
              onClick={() => setMobileMenuOpen(false)}
              className={`block px-4 py-3 rounded-lg font-medium flex items-center gap-2 transition-all ${
                isActive('/archived')
                  ? 'bg-purple-500 text-white'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <Archive className="w-5 h-5" />
              Archived
            </Link>

            <Link
              to="/notifications"
              onClick={() => setMobileMenuOpen(false)}
              className={`block px-4 py-3 rounded-lg font-medium flex items-center gap-2 transition-all ${
                isActive('/notifications')
                  ? 'bg-red-500 text-white'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <Bell className="w-5 h-5" />
              Notifications
              {notificationCount > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5">
                  {notificationCount}
                </span>
              )}
            </Link>

            {isAdmin && (
              <>
                <Link
                  to="/admin/users"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-3 rounded-lg font-medium flex items-center gap-2 transition-all ${
                    isActive('/admin/users')
                      ? 'bg-amber-500 text-white'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <Users className="w-5 h-5" />
                  Users
                </Link>

                {/* Mobile Settings Section */}
                <div className="mt-4 pt-4 border-t border-slate-700">
                  <p className="px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Data Management
                  </p>
                  
                  <button
                    onClick={() => {
                      handleBackup();
                      setMobileMenuOpen(false);
                    }}
                    disabled={isBackingUp}
                    className="w-full px-4 py-3 text-left text-slate-300 hover:bg-slate-800 hover:text-white rounded-lg transition-colors flex items-center gap-3 disabled:opacity-50"
                  >
                    <Download className="w-5 h-5 text-green-400" />
                    <div>
                      <p className="text-sm font-medium">
                        {isBackingUp ? 'Creating Backup...' : 'Backup Data'}
                      </p>
                      <p className="text-xs text-slate-500">Export encrypted backup</p>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      handleRestoreClick();
                      setMobileMenuOpen(false);
                    }}
                    disabled={isRestoring}
                    className="w-full px-4 py-3 text-left text-slate-300 hover:bg-slate-800 hover:text-white rounded-lg transition-colors flex items-center gap-3 disabled:opacity-50"
                  >
                    <Upload className="w-5 h-5 text-blue-400" />
                    <div>
                      <p className="text-sm font-medium">
                        {isRestoring ? 'Restoring...' : 'Restore Data'}
                      </p>
                      <p className="text-xs text-slate-500">Import encrypted backup</p>
                    </div>
                  </button>
                </div>
              </>
            )}

            <button
              onClick={() => {
                setMobileMenuOpen(false);
                handleLogout();
              }}
              className="w-full px-4 py-3 bg-red-600 hover:bg-red-500 text-white rounded-lg font-medium flex items-center gap-2 transition-colors mt-4"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}